import React from "react";
import "./BottomFloatingCapsule.css";
import { toPersianDigits } from "../../utils/toPersianDigits";
import { watchMediaQuery } from "../../utils/mediaQuery";

const BottomFloatingCapsule = ({
  isFocusMode,
  onScrollToTop,
  onReadAll,
  onExitFocus,
  isAtTop,
  totalUnreadCount = 0,
  focusedUnreadCount = 0,
  isModalOpen,
  isMostVisitedMode = false,
  onCloseModal,
  onClearSaved,
  isSavedMode = false,
  onOpenSaved,
  onCloseSaved,
  savedCount = 0,
  middleComponent,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    return watchMediaQuery("(max-width: 767px)", setIsMobile);
  }, []);

  const unreadNumber = isFocusMode ? focusedUnreadCount : totalUnreadCount;

  const canReadAll = isSavedMode
    ? savedCount > 0
    : !isModalOpen && unreadNumber > 0;

  const showCloseModal = isModalOpen;
  const showCloseSaved = !isModalOpen && isSavedMode;
  const showExitFocus = !isModalOpen && !isSavedMode && isFocusMode;
  const showOpenSaved = !showCloseModal && !showCloseSaved && !showExitFocus;
  const isLeftButtonDisabled = showOpenSaved && savedCount === 0;

  const showMiddleCapsule =
    isMobile &&
    !isFocusMode &&
    !isModalOpen &&
    !isSavedMode &&
    middleComponent;

  const showLeftButton = !showOpenSaved || !showMiddleCapsule;

  const getLeftButtonLabel = () => {
    if (showCloseModal) return "بستن";
    if (showCloseSaved) return "بستن ذخیره‌شده‌ها";
    if (showExitFocus) return "خروج از فوکوس";
    if (showOpenSaved) return "نمایش ذخیره‌شده‌ها";
    return "";
  };

  const leftButtonLabel = getLeftButtonLabel();

  const handleLeftClick = () => {
    if (showCloseModal) {
      onCloseModal?.();
    } else if (showCloseSaved) {
      onCloseSaved?.();
    } else if (showExitFocus) {
      onExitFocus?.();
    } else if (showOpenSaved) {
      onOpenSaved?.();
    }
  };

  const handleReadAllClick = (event) => {
    event.currentTarget.blur();

    if (isSavedMode) {
      onClearSaved?.();
      return;
    }

    onReadAll?.();
  };

  const readAllTitle = isSavedMode
    ? "پاک کردن ذخیره‌شده‌ها"
    : unreadNumber === 0
    ? "همه خوانده شده‌اند"
    : isFocusMode
    ? "همهٔ اخبار این منبع را خواندم"
    : "همه را خواندم";

  return (
    <div
      className={`bottom-float-container${
        isMostVisitedMode ? " bottom-float-container--most-visited" : ""
      }${isSavedMode ? " bottom-float-container--saved" : ""}`}
    >
      <button
        type="button"
        className={`read-all-fab${
          isSavedMode ? " read-all-fab--unsave" : ""
        }${!canReadAll ? " read-all-fab--disabled" : ""}${
          isMobile ? " read-all-fab--mobile" : ""
        }`}
        onClick={canReadAll ? handleReadAllClick : undefined}
        disabled={!canReadAll}
        title={readAllTitle}
        aria-label={readAllTitle}
        aria-disabled={!canReadAll}
      >
        {isSavedMode ? (
          <svg
            viewBox="0 0 24 24"
            className="ui-svg-icon read-all-icon read-all-icon--unsave"
            aria-hidden="true"
            focusable="false"
          >
            <path
              className="read-all-icon--unsave-bookmark"
              d="M7 4.5C7 3.7 7.7 3 8.5 3h7c.8 0 1.5.7 1.5 1.5V20l-5-3.5L7 20V4.5Z"
            />
            <line
              className="read-all-icon--unsave-slash read-all-icon--unsave-slash-fg"
              x1="4"
              y1="19"
              x2="21"
              y2="2"
            />
            <line
              className="read-all-icon--unsave-slash read-all-icon--unsave-slash-bg"
              x1="3"
              y1="17"
              x2="20"
              y2="0"
            />
          </svg>
        ) : (
          <svg
            viewBox="0 0 28 24"
            className="ui-svg-icon read-all-icon"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M2 13l5 5 9-9" opacity="0.7" />
            <path d="M8 13l5 5 12-12" />
          </svg>
        )}

        {!isModalOpen && !isSavedMode && unreadNumber > 0 && (
          <span className="read-all-badge">
            {unreadNumber > 999 ? "۹۹۹+" : toPersianDigits(unreadNumber)}
          </span>
        )}
      </button>

      {showMiddleCapsule && (
        <div className="bottom-capsule-middle">{middleComponent}</div>
      )}

      <div className="bottom-capsule bottom-capsule--actions">
        <button
          type="button"
          className={`capsule-btn ${isAtTop ? "capsule-btn--disabled" : ""}`}
          onClick={!isAtTop ? onScrollToTop : undefined}
          title="برو به بالا"
          aria-label="برو به بالا"
          disabled={isAtTop}
        >
          <svg
            viewBox="0 0 24 24"
            className="ui-svg-icon"
            aria-hidden="true"
            focusable="false"
          >
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>

        {showLeftButton && (
          <button
            type="button"
            className={`capsule-btn ${
              isLeftButtonDisabled ? "capsule-btn--disabled" : ""
            }`}
            onClick={!isLeftButtonDisabled ? handleLeftClick : undefined}
            title={leftButtonLabel}
            aria-label={leftButtonLabel}
            disabled={isLeftButtonDisabled}
          >
            {showOpenSaved ? (
              <svg
                viewBox="0 0 24 24"
                className="ui-svg-icon capsule-save-icon"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M7 4.5C7 3.7 7.7 3 8.5 3h7c.8 0 1.5.7 1.5 1.5V20l-5-3.5L7 20V4.5Z" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                className="ui-svg-icon"
                aria-hidden="true"
                focusable="false"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default BottomFloatingCapsule;